// File: apps/web/src/App.tsx

import { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';
import { ScoutRoute } from './components/auth/ScoutRoute';
import { InstallPrompt } from './components/pwa/InstallPrompt';

// Lazy load pages for better initial bundle size
const HomePage = lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const StreamListPage = lazy(() => import('./pages/StreamListPage').then(m => ({ default: m.StreamListPage })));
const StreamViewPage = lazy(() => import('./pages/StreamViewPage').then(m => ({ default: m.StreamViewPage })));
const CreateStreamPage = lazy(() => import('./pages/CreateStreamPage').then(m => ({ default: m.CreateStreamPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const EditProfilePage = lazy(() => import('./pages/EditProfilePage').then(m => ({ default: m.EditProfilePage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const TeamsListPage = lazy(() => import('./pages/TeamsListPage').then(m => ({ default: m.TeamsListPage })));
const CreateTeamPage = lazy(() => import('./pages/CreateTeamPage').then(m => ({ default: m.CreateTeamPage })));
const TournamentsListPage = lazy(() => import('./pages/TournamentsListPage').then(m => ({ default: m.TournamentsListPage })));
const CreateTournamentPage = lazy(() => import('./pages/CreateTournamentPage').then(m => ({ default: m.CreateTournamentPage })));
const SchedulePage = lazy(() => import('./pages/SchedulePage').then(m => ({ default: m.SchedulePage })));
const DiscoverPage = lazy(() => import('./pages/DiscoverPage').then(m => ({ default: m.DiscoverPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const TeamDetailPage = lazy(() => import('./pages/TeamDetailPage').then(m => ({ default: m.TeamDetailPage })));
const TournamentDetailPage = lazy(() => import('./pages/TournamentDetailPage').then(m => ({ default: m.TournamentDetailPage })));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage').then(m => ({ default: m.SubscriptionPage })));
const EarningsPage = lazy(() => import('./pages/EarningsPage').then(m => ({ default: m.EarningsPage })));
const SearchPage = lazy(() => import('./pages/SearchPage').then(m => ({ default: m.SearchPage })));
const ClipsPage = lazy(() => import('./pages/ClipsPage').then(m => ({ default: m.ClipsPage })));
const ClipViewPage = lazy(() => import('./pages/ClipViewPage').then(m => ({ default: m.ClipViewPage })));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const AnalyticsDashboardPage = lazy(() => import('./pages/AnalyticsDashboardPage'));
const RecruitmentPage = lazy(() => import('./pages/RecruitmentPage'));
const PracticeSchedulerPage = lazy(() => import('./pages/PracticeSchedulerPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const ParentPortalPage = lazy(() => import('./pages/ParentPortalPage'));
const RosterManagementPage = lazy(() => import('./pages/RosterManagementPage'));
const ManualsPage = lazy(() => import('./pages/ManualsPage'));

// VOD pages
const VODLibraryPage = lazy(() => import('./pages/VODLibraryPage'));
const VODWatchPage = lazy(() => import('./pages/VODWatchPage'));

// Live streaming pages (external API)
const GoLivePage = lazy(() => import('./pages/GoLivePage').then(m => ({ default: m.GoLivePage })));
const WatchPage = lazy(() => import('./pages/WatchPage').then(m => ({ default: m.WatchPage })));

// Admin pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const UserManagement = lazy(() => import('./pages/admin/UserManagement').then(m => ({ default: m.UserManagement })));
const TeamManagement = lazy(() => import('./pages/admin/TeamManagement').then(m => ({ default: m.TeamManagement })));
const StreamManagement = lazy(() => import('./pages/admin/StreamManagement').then(m => ({ default: m.StreamManagement })));
const TournamentManagement = lazy(() => import('./pages/admin/TournamentManagement').then(m => ({ default: m.TournamentManagement })));
const ReportQueue = lazy(() => import('./pages/admin/ReportQueue').then(m => ({ default: m.ReportQueue })));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/AdminAnalyticsPage').then(m => ({ default: m.AdminAnalyticsPage })));

// Scout pages
const ScoutDashboard = lazy(() => import('./pages/scout/ScoutDashboard').then(m => ({ default: m.ScoutDashboard })));
const WatchlistPage = lazy(() => import('./pages/scout/WatchlistPage').then(m => ({ default: m.WatchlistPage })));
const PlayerComparisonPage = lazy(() => import('./pages/scout/PlayerComparisonPage').then(m => ({ default: m.PlayerComparisonPage })));
const PlayerSearchPage = lazy(() => import('./pages/scout/PlayerSearchPage').then(m => ({ default: m.PlayerSearchPage })));
const NotesPage = lazy(() => import('./pages/scout/NotesPage').then(m => ({ default: m.NotesPage })));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4"></div>
        <p className="text-gray-400">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  const { isAuthenticated, getCurrentUser } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      getCurrentUser();
    }
  }, [isAuthenticated, getCurrentUser]);

  // Pages that should not use the Layout component
  const noLayoutPages = ['/', '/login', '/register'];
  const noLayoutPrefixes = ['/watch'];
  const useLayout = !noLayoutPages.includes(location.pathname) &&
    !noLayoutPrefixes.some(prefix => location.pathname.startsWith(prefix));

  const routes = (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes without layout */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/watch/:eventId" element={<WatchPage />} />

        {/* Public routes with layout */}
        <Route path="/streams" element={<StreamListPage />} />
        <Route path="/streams/:id" element={<StreamViewPage />} />
        <Route path="/profile/:userId" element={<ProfilePage />} />
        <Route path="/teams" element={<TeamsListPage />} />
        <Route path="/teams/:teamId" element={<TeamDetailPage />} />
        <Route path="/tournaments" element={<TournamentsListPage />} />
        <Route path="/tournaments/:tournamentId" element={<TournamentDetailPage />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/discover" element={<DiscoverPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/clips" element={<ClipsPage />} />
        <Route path="/clips/:id" element={<ClipViewPage />} />
        <Route path="/recruitment" element={<RecruitmentPage />} />
        <Route path="/recruitment/:id" element={<RecruitmentPage />} />
        <Route path="/vod" element={<VODLibraryPage />} />
        <Route path="/vod/:id" element={<VODWatchPage />} />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/go-live" element={<GoLivePage />} />
          <Route path="/streams/create" element={<CreateStreamPage />} />
          <Route path="/teams/create" element={<CreateTeamPage />} />
          <Route path="/tournaments/create" element={<CreateTournamentPage />} />
          <Route path="/profile/:userId/edit" element={<EditProfilePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/earnings" element={<EarningsPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/:conversationId" element={<MessagesPage />} />
          <Route path="/analytics" element={<AnalyticsDashboardPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/practices" element={<PracticeSchedulerPage />} />
          <Route path="/practices/team/:teamId" element={<PracticeSchedulerPage />} />
          <Route path="/parent" element={<ParentPortalPage />} />
          <Route path="/teams/:teamId/roster" element={<RosterManagementPage />} />
          <Route path="/manual" element={<ManualsPage />} />
        </Route>

        {/* Admin routes (protected) */}
        <Route element={<AdminRoute />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<UserManagement />} />
          <Route path="/admin/teams" element={<TeamManagement />} />
          <Route path="/admin/streams" element={<StreamManagement />} />
          <Route path="/admin/tournaments" element={<TournamentManagement />} />
          <Route path="/admin/reports" element={<ReportQueue />} />
          <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
        </Route>

        {/* Scout routes (protected) */}
        <Route element={<ScoutRoute />}>
          <Route path="/scout" element={<ScoutDashboard />} />
          <Route path="/scout/watchlist" element={<WatchlistPage />} />
          <Route path="/scout/compare" element={<PlayerComparisonPage />} />
          <Route path="/scout/search" element={<PlayerSearchPage />} />
          <Route path="/scout/notes" element={<NotesPage />} />
        </Route>

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );

  return (
    <>
      {useLayout ? <Layout>{routes}</Layout> : routes}
      <InstallPrompt />
    </>
  );
}

export default App;
